import { context } from '@shared-services/context-service';
import { localizationService } from '@shared-services/localization-service';
import { getWindowInBrowser } from 'client/RuntimeWindow';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { BuyBookingPayload } from 'client/widget-components/ecom/types/EcomSDK';
import { logBookingError } from './loggingUtils';
import strings from './strings';
import {
    BookerContextType,
    Booking,
    BookingStatus,
    CalEventData,
    CreateBookingInput,
    DesignStyle,
    FeatureMetadata,
    PricingType,
} from './types';

const str = localizationService.str;

// all locales grabbed from https://github.com/calcom/cal.com/tree/main/apps/web/public/static/locales
const CAL_PROVIDER_LANGUAGES_MAP: Record<string, string> = {
    ar: 'ar',
    az: 'az',
    bg: 'bg',
    cs: 'cs',
    da: 'da',
    de: 'de',
    el: 'el',
    en: 'en',
    es: 'es',
    eu: 'eu',
    fr: 'fr',
    he: 'he',
    hr: 'hr',
    hu: 'hu',
    id: 'id',
    it: 'it',
    iw: 'iw',
    ja: 'ja',
    ko: 'ko',
    nl: 'nl',
    no: 'no',
    pl: 'pl',
    pt: 'pt',
    'pt-br': 'pt-BR',
    ro: 'ro',
    ru: 'ru',
    sk: 'sk',
    sr: 'sr',
    sv: 'sv',
    ta: 'ta',
    tr: 'tr',
    uk: 'uk',
    vi: 'vi',
    zh: 'zh-CN',
    'zh-cn': 'zh-CN',
    'zh-tw': 'zh-TW',
    fi: 'fi',
    lv: 'lv',
    is: 'is',
    ca: 'ca',
    nb: 'nb',
    // absent from calcom locales
    // lt: 'lt',
};

const DEFAULT_LANGUAGE = 'en';

export const resolveCalProviderLanguage = () => {
    // if we are in server return default language.
    // locale code is not available on server for now, but could be postponed since calcom atoms renders on client only
    if (context.isServer) {
        return DEFAULT_LANGUAGE;
    }

    const { dmAPI } = getWindowInBrowser();

    const currentLocale = dmAPI?.getSiteCurrentLocale?.() || '';

    // get short language code
    const [shortLanguage] = currentLocale.split('-');

    return (
        CAL_PROVIDER_LANGUAGES_MAP[currentLocale.toLowerCase()] ||
        CAL_PROVIDER_LANGUAGES_MAP[shortLanguage] ||
        DEFAULT_LANGUAGE
    );
};

export const customClassNames = {
    atomsWrapper: 'atoms-wrapper',
    bookerContainer: 'booker-container',
    eventMetaCustomClassNames: {
        eventMetaContainer: 'event-meta-container',
        eventMetaTitle: 'event-meta-title',
        eventMetaTimezoneSelect: 'event-meta-timezone-select',
        eventMetaChildren: 'event-meta-children', // TODO: ask cal to fix it (not applied) + add more class names
    },
    datePickerCustomClassNames: {
        datePickerContainer: 'date-picker-container',
        datePickerTitle: 'date-picker-title',
        datePickerDays: 'date-picker-days',
        datePickerDate: 'date-picker-date',
        datePickerDatesActive: 'date-picker-dates-active',
        datePickerToggle: 'date-picker-toggle',
    },
    availableTimeSlotsCustomClassNames: {
        availableTimeSlotsContainer: 'available-time-slots-container',
        availableTimeSlotsHeaderContainer:
            'available-time-slots-header-container',
        availableTimeSlotsTitle: 'available-time-slots-title',
        availableTimeSlotsTimeFormatToggle:
            'available-time-slots-time-format-toggle',
        availableTimes: 'available-times',
    },
    confirmStep: {
        confirmButton: 'confirm-button',
        backButton: 'back-button ',
    },
};

export const getBookerClassNames = (designStyle: DesignStyle) => ({
    ...customClassNames,
    confirmStep: {
        ...customClassNames.confirmStep,
        backButton: `${customClassNames.confirmStep.backButton} ${
            designStyle === DesignStyle.SITE_THEME ? 'button_2' : ''
        }`,
    },
});

export const isBookingValid = (status: BookingStatus) => {
    if (!status) return false;
    return ![BookingStatus.CANCELLED, BookingStatus.REJECTED].includes(status);
};

export const isBookingAccepted = (status?: BookingStatus) => {
    if (!status) return false;
    return status === BookingStatus.ACCEPTED;
};

export const isBookingPending = (status?: BookingStatus) => {
    if (!status) return false;
    return status === BookingStatus.PENDING;
};

const validators = {
    isNonEmptyString: (value: any): value is string => {
        return !!value && typeof value === 'string';
    },
    isInteger: (value: any): value is number => {
        return Number.isInteger(value);
    },
    isNumber: (value: any): value is number => {
        return typeof value === 'number';
    },
    oneOf:
        <T>(options: T[]) =>
        (value: any): value is T => {
            return options.includes(value);
        },
};

type BookerParamKey = keyof Pick<
    BookerContextType,
    | 'clientId'
    | 'apiUrl'
    | 'eventSlug'
    | 'teamId'
    | 'language'
    | 'organizationId'
>;

const bookerParamRules: Record<BookerParamKey, (value: unknown) => boolean> = {
    clientId: validators.isNonEmptyString,
    apiUrl: validators.isNonEmptyString,
    eventSlug: validators.isNonEmptyString,
    teamId: validators.isInteger,
    language: validators.isNonEmptyString,
    organizationId: validators.isInteger,
};

export const validateBookerReceivedParams = (
    bookerContext: BookerContextType,
    isActive: boolean
) => {
    // Only validate when booking is active
    if (!isActive) {
        return;
    }

    const invalidParams: string[] = [];

    (
        Object.entries(bookerParamRules) as [
            BookerParamKey,
            (value: unknown) => boolean
        ][]
    ).forEach(([param, validator]) => {
        if (!validator(bookerContext[param])) {
            invalidParams.push(param);
        }
    });

    if (invalidParams.length > 0) {
        logBookingError('Failed to validate booker received params', {
            additionalInfoToLog: bookerContext,
            invalidParams,
        });
    }
};

export const bookingObjToEcomConverter = (
    bookingData: CreateBookingInput,
    calEventData: CalEventData
): BuyBookingPayload => {
    const hasMultipleDurations = Array.isArray(
        calEventData?.metadata?.multipleDuration
    );

    return {
        event_name: calEventData?.title || '',
        event_type_id: bookingData.eventTypeId,
        slot_start: bookingData.start,
        time_zone: bookingData.timeZone,
        metadata: bookingData.metadata,
        fields_responses: bookingData.responses,
        customer_name: bookingData.responses.name as string,
        customer_email: bookingData.responses.email as string,
        ...(hasMultipleDurations && {
            slot_duration: bookingData.slot_duration,
        }),
    };
};

export const shouldUsePaidBooking = ({ currentEvent }: BookerContextType) => {
    return currentEvent?.priceInfo?.pricingType === PricingType.PAID;
};

export const getBookerMetadata = (
    {
        eventIdentifier,
        eventSlug,
        currentEvent,
        isBookingDryRun,
    }: BookerContextType,
    calEventData: CalEventData | null
): Record<string, string> => {
    const {
        pricingType,
        formattedBasePrice,
        formattedTaxIncludedPrice,
        taxIncluded,
    } = currentEvent?.priceInfo || {};

    return {
        calEventId: calEventData?.id ? calEventData.id.toString() : '',
        calEventSlug: eventSlug || '',
        eventId: eventIdentifier,
        eventTitle: calEventData?.title || '',
        pricingType: pricingType || PricingType.FREE,
        formattedBasePrice: formattedBasePrice || '',
        formattedTaxIncludedPrice: formattedTaxIncludedPrice || '',
        taxIncluded: (taxIncluded || false).toString(),
        originalBookingPath: context.isBrowser ? window.location.pathname : '',
        isTest: isBookingDryRun.toString(),
    };
};

export const createDryRunBookingPayload = (): Booking => ({
    title: '',
    userPrimaryEmail: '',
    startTime: new Date().toISOString(),
    user: {
        name: '',
    },
    uid: '',
    endTime: new Date(),
    attendees: [],
    status: BookingStatus.ACCEPTED,
});

export const isBookingDryRun = (
    editor: ReturnType<typeof useEditor>,
    featureMetadata: FeatureMetadata
) => {
    return (
        editor.isInPreviewOrEditor() ||
        context.isServer ||
        !featureMetadata.isNativeBookingPublishingAvailable
    );
};

export const getFormattedPriceStr = (bookerContext: BookerContextType) => {
    const isPaidBooking = shouldUsePaidBooking(bookerContext);

    let priceString = localizationService.str(strings.freeBookingPrice);

    const currentEventPriceInfo = bookerContext.currentEvent?.priceInfo;

    if (isPaidBooking && currentEventPriceInfo?.basePrice) {
        priceString = currentEventPriceInfo.taxIncluded
            ? currentEventPriceInfo?.formattedTaxIncludedPrice || ''
            : currentEventPriceInfo?.formattedBasePrice || '';
    }

    return priceString || '';
};

const extractTimeZoneName = (
    formatter: Intl.DateTimeFormat,
    date: Date
): string =>
    formatter
        .formatToParts(date)
        .filter((part) => part.type === 'timeZoneName')
        .map((part) => part.value)
        .join('')
        .trim();

export const getLocaleTimeZoneName = (
    date: Date,
    locale?: string | string[],
    options?: Intl.DateTimeFormatOptions
) => {
    const baseOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...options,
    };

    return (
        extractTimeZoneName(
            new Intl.DateTimeFormat(locale, {
                ...baseOptions,
                timeZoneName: 'long',
            }),
            date
        ) || Intl.DateTimeFormat().resolvedOptions().timeZone
    );
};

// Cal.com is adding `+{string}` to mails, that have coincidence
// example: example@mail.com ---> example+jnsdc132DSf@mail.com
// cal bug: https://duda.slack.com/lists/T02RF76D2/F09SV1SHML4?record_id=Rec09V484MH6D
export const sanitizeMail = (mailToSanitize: string): string =>
    mailToSanitize.replace(/(\+.*)@/, '@');

// need localized title. temp solution
// cal bug: https://duda.slack.com/lists/T02RF76D2/F09SV1SHML4?record_id=Rec09UJUE9KTM
export const buildBookingTitle = (bookingData: Booking): string => {
    const eventTitle = bookingData.metadata?.eventTitle;
    const staffMemberName = bookingData.user.name;
    const attendeeNames = bookingData.attendees
        .map((attendee) => attendee.name)
        .join(', ');

    // if any of the values is missing, return the original title
    if (!eventTitle || !staffMemberName || !attendeeNames) {
        return bookingData.title;
    }

    return str(strings.bookingTitle, {
        eventTitle,
        staffMemberName,
        attendeeNames,
    });
};
